import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { Select } from "@/components/Select";
import { logsApi } from "@/lib/api";

type LogSource = "configured" | "file" | "mongodb";

export const Logs: React.FC = () => {
    const { t } = useTranslation("global");
    const [source, setSource] = useState<LogSource>("configured");
    const [result, setResult] = useState<{ entries?: unknown[]; source?: string }>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);

        logsApi.list(source)
            .then((data) => {
                if (active) {
                    setResult(data);
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [source]);

    return (
        <div className="page space-y-6">
            <PageHeader title={t("logs.title")} subtitle={t("logs.subtitle")} />

            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-medium text-gray-700">{t("logs.source")}</div>
                <Select
                    value={source}
                    onChange={(value) => setSource(value as LogSource)}
                    options={[
                        { value: "configured", label: t("logs.sources.configured") },
                        { value: "file", label: t("logs.sources.file") },
                        { value: "mongodb", label: t("logs.sources.mongodb") },
                    ]}
                />
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-4 text-sm text-gray-500">
                    {t("logs.active_source")}: {result.source ?? source}
                </div>
                <div className="max-h-[65vh] overflow-auto p-5">
                    {loading && <div className="text-sm text-gray-500">{t("common.loading")}</div>}
                    {!loading && !(result.entries?.length) && (
                        <div className="text-sm text-gray-500">{t("logs.empty")}</div>
                    )}
                    {!loading && result.entries?.map((entry, index) => (
                        <pre key={index} className="mb-3 whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                            {typeof entry === "string" ? entry : JSON.stringify(entry, null, 2)}
                        </pre>
                    ))}
                </div>
            </div>
        </div>
    );
};
